/**
 * Datadog RUM (Real User Monitoring) — site us5, misma org que el log
 * forwarder de Railway (server/datadogLogForwarder.ts).
 *
 * Diseño:
 *  - Gateado por env: sin VITE_DD_RUM_APPLICATION_ID + VITE_DD_RUM_CLIENT_TOKEN
 *    es un no-op total (local / brands sin RUM). El client token es público
 *    por diseño (como el token de PostHog) — no es un secreto.
 *  - Dynamic import: el SDK (~50KB gzip) no entra al bundle inicial ni compite
 *    con el LCP del landing; se carga en requestIdleCallback.
 *  - Session Replay APAGADO (sessionReplaySampleRate: 0): PostHog ya graba
 *    replays — duplicarlo es costo sin señal nueva.
 *  - Expone window.DD_RUM: src/lib/adminObservability.ts ya emite
 *    addAction("admin_api_request") / addError() de forma opcional contra ese
 *    global — con esto esos hooks quedan vivos.
 *
 * Activación — vía Worker edge config (fuente REAL en prod) o env build-time:
 *  GOTCHA Railway: el build Docker NO ve las variables del servicio (mismo
 *  motivo por el que la Clerk key viaja por __EDGE_RUNTIME_CONFIG__ y las
 *  migraciones SQL se saltean en build). Las VITE_DD_RUM_* de Railway son
 *  inertes — la config real la inyecta el Worker (wrangler.toml [vars] →
 *  buildEdgeRuntimeConfig → ddRumApplicationId/ddRumClientToken).
 *  - ddRumApplicationId / VITE_DD_RUM_APPLICATION_ID  — UUID de la RUM app
 *  - ddRumClientToken   / VITE_DD_RUM_CLIENT_TOKEN    — client token (pub…)
 *  - ddRumSampleRate    / VITE_DD_RUM_SAMPLE_RATE     — % sesiones (default 100)
 */
import { BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { edgeRuntimeString } from "@/lib/edgeRuntimeConfig";

let started = false;

/**
 * Solo monitorear PRODUCCIÓN: el dominio real de la marca (www.delfuocampo.com
 * y subdominios). localhost, previews y la URL directa de Railway NO emiten —
 * sesiones de dev/QA ensucian Core Web Vitals y cuentan contra la cuota RUM.
 * Además en esos hosts no corre el Worker → no hay edge config → no-op igual;
 * este gate lo hace explícito y cubre el caso de VITE_* seteadas en local.
 */
function isProductionHost(hostname: string): boolean {
  if (!BRAND_SITE_HOST) return false;
  const h = hostname.toLowerCase();
  return h === BRAND_SITE_HOST || h.endsWith(`.${BRAND_SITE_HOST}`);
}

export function initDatadogRum(): void {
  if (started) return;
  if (typeof window === "undefined") return;
  if (!isProductionHost(window.location.hostname)) return;
  // Lazy: el script inline del Worker (__EDGE_RUNTIME_CONFIG__) ya corrió para
  // cuando el bundle evalúa, pero leemos acá igual para no depender del orden.
  const APPLICATION_ID = (
    edgeRuntimeString("ddRumApplicationId") ||
    (import.meta.env.VITE_DD_RUM_APPLICATION_ID as string) ||
    ""
  ).trim();
  const CLIENT_TOKEN = (
    edgeRuntimeString("ddRumClientToken") ||
    (import.meta.env.VITE_DD_RUM_CLIENT_TOKEN as string) ||
    ""
  ).trim();
  const SAMPLE_RATE = Number(
    edgeRuntimeString("ddRumSampleRate") ||
      import.meta.env.VITE_DD_RUM_SAMPLE_RATE ||
      100,
  );
  if (!APPLICATION_ID || !CLIENT_TOKEN) return; // no configurado → no-op
  started = true;

  const start = () => {
    void import("@datadog/browser-rum")
      .then(({ datadogRum }) => {
        datadogRum.init({
          applicationId: APPLICATION_ID,
          clientToken: CLIENT_TOKEN,
          site: "us5.datadoghq.com",
          service: `${BRAND_SOURCE_KEY}-web`,
          env: import.meta.env.MODE === "production" ? "production" : "development",
          sessionSampleRate:
            Number.isFinite(SAMPLE_RATE) && SAMPLE_RATE >= 0 && SAMPLE_RATE <= 100
              ? SAMPLE_RATE
              : 100,
          // Replay es de PostHog — acá solo métricas/errores/recursos.
          sessionReplaySampleRate: 0,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          defaultPrivacyLevel: "mask-user-input",
        });
        // Bridge para adminObservability.ts (window.DD_RUM?.addAction/addError).
        window.DD_RUM = {
          addAction: (name, context) => datadogRum.addAction(name, context),
          addError: (error, context) => datadogRum.addError(error, context),
        };
      })
      .catch(() => {
        /* telemetría nunca rompe la app (ad-blockers, red) */
      });
  };

  // Idle: no competir con el primer frame del video landing.
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 5_000 });
  } else {
    window.setTimeout(start, 2_000);
  }
}
